import { Highlight } from '@/components/HighlightText';
import { formatNumber, formatTimestamp, utils } from '@/helpers';
import type { Campaign, CampaignUID } from '@/types/campaigns';
import type {
	CampaignsType,
	CoMarketingApprovalStatus,
	CoMarketingTemplateWithCampaign,
	InstalledCoMarketingTemplate,
	InstalledCoMarketingTemplateWithCampaign,
	SponsoredCoMarketingTemplateWithCampaign,
} from '@/types';
import { Icon, Tag, TagProps, Td, Toggle, Typography } from '@alpine-iq/ui-library';
import React from 'react';
import { CampaignChannelIcons } from '@/pages/campaigns/components/table-cells/CampaignChannelIcon';
import { useCoMarketingStore, useSponsoringTemplatesStore } from '@/stores';
import { getCoMarketingBudgetForUser } from '../coMarketing.utils';

const TableKey = 'campaignsTable';

type RenderProps = {
	search?: string;
};

export type CoMarketingColumnConfig<T extends CoMarketingTemplateWithCampaign> = {
	label: string;
	sortable?: boolean;
	localKey?: string;
	enabled: () => boolean;
	setEnabled: (value: boolean) => void;
	settings: () => ColumnSaveSetting;
	setSettings: (value: Partial<ColumnSaveSetting>) => void;
	clearSettings: () => void;
	render: (campaign: T, props: RenderProps) => React.ReactNode;
};

export type ColumnSaveSetting = {
	enabled?: boolean;
	columnOrder?: number;
};

const getLocal = (key: string, defaultValue: ColumnSaveSetting) => {
	const local = utils.local.get(key, defaultValue);
	return { ...defaultValue, ...local };
};

const setLocal = (key: string, value: ColumnSaveSetting) => {
	utils.local.set(key, value);
};

export type CoMarketingColumnKeys =
	| keyof CoMarketingTemplateWithCampaign
	| 'name'
	| 'created'
	| 'retailers'
	| 'budget'
	| 'installations'
	| 'rejections'
	| 'id'
	| 'channels'
	| 'status';

export const getColumnDefaults = (
	name: CoMarketingColumnKeys,
	isDefault?: boolean,
	columnOrder?: number,
): Omit<CoMarketingColumnConfig<CoMarketingTemplateWithCampaign>, 'label' | 'render' | 'sortable'> => {
	return {
		settings: () => getLocal(`${TableKey}.${name}`, { enabled: isDefault, columnOrder }),
		enabled: () => CoMarketingTableBaseHeaders?.[name]?.settings?.()?.enabled ?? isDefault ?? false,
		setEnabled: (value) => CoMarketingTableBaseHeaders?.[name]?.setSettings?.({ enabled: value }),
		setSettings: (value) => setLocal(`${TableKey}.${name}`, { ...getLocal(`${TableKey}.${name}`, { enabled: true }), ...value }),
		clearSettings: () => setLocal(`${TableKey}.${name}`, { enabled: isDefault }),
	};
};

export const CoMarketingTableBaseHeaders: Partial<Record<CoMarketingColumnKeys, CoMarketingColumnConfig<CoMarketingTemplateWithCampaign>>> = {
	name: {
		sortable: true,
		label: 'Name',
		...getColumnDefaults('name', true),
		render(template, props) {
			return (
				<Td>
					<Highlight
						text={template.campaign?.name ?? ''}
						search={props?.search ?? ''}
					/>
				</Td>
			);
		},
	},

	created: {
		sortable: true,
		label: 'Created',
		...getColumnDefaults('created', true),
		render(template, _) {
			return (
				<Td>
					<Typography>{formatTimestamp(template.created, { hideTime: true })}</Typography>
					<Typography variant="body-sm">
						{formatTimestamp(template.created, {
							month: false,
							day: false,
							year: false,
						})}
					</Typography>
				</Td>
			);
		},
	},

	budget: {
		sortable: true,
		label: 'Budget',
		...getColumnDefaults('budget', true),
		render(template, props) {
			return (
				<Td>
					<Typography>{getCoMarketingBudgetForUser(template, utils.uid)}</Typography>
				</Td>
			);
		},
	},

	id: {
		sortable: true,
		label: 'ID',
		...getColumnDefaults('id', true),
		render(template, props) {
			return (
				<Td>
					<Typography>
						<Highlight
							text={`${template.id}`}
							search={props?.search ?? ''}
						/>
					</Typography>
				</Td>
			);
		},
	},

	channels: {
		sortable: true,
		label: 'Preposed Channels',
		...getColumnDefaults('channels', true),
		render(template, _) {
			return (
				<Td>
					<CampaignChannelIcons campaign={template?.campaign} />
				</Td>
			);
		},
	},
} as const;

export type CoMarketingSponsoringColumnKeys =
	| keyof SponsoredCoMarketingTemplateWithCampaign
	| 'name'
	| 'created'
	| 'retailers'
	| 'installers'
	| 'id'
	| 'channels'
	| 'status';

export const CoMarketingTableSponsoringHeaders: Partial<Record<CoMarketingSponsoringColumnKeys, CoMarketingColumnConfig<SponsoredCoMarketingTemplateWithCampaign>>> =
	{
		status: {
			sortable: true,
			label: 'Status',
			...getColumnDefaults('status', true),
			render(template, _) {
				return (
					<Td>
						<Toggle
							toggled={template.published}
							onChange={() => {
								useSponsoringTemplatesStore.getState().setTemplatePublishStatus(!template.published, template.id);
							}}
						/>
					</Td>
				);
			},
		},
		name: {
			sortable: true,
			label: 'Name',
			...getColumnDefaults('name', true),
			render(template, props) {
				return (
					<Td>
						<Highlight
							text={template.campaign?.name ?? ''}
							search={props?.search ?? ''}
						/>
					</Td>
				);
			},
		},

		created: {
			sortable: true,
			label: 'Created',
			...getColumnDefaults('created', true),
			render(template, _) {
				return (
					<Td>
						<Typography>{formatTimestamp(template.created, { hideTime: true })}</Typography>
						<Typography variant="body-sm">
							{formatTimestamp(template.created, {
								month: false,
								day: false,
								year: false,
							})}
						</Typography>
					</Td>
				);
			},
		},

		retailers: {
			sortable: true,
			label: 'Retailers',
			...getColumnDefaults('retailers', true),
			render(template, props) {
				return (
					<Td>
						<Typography>{formatNumber(Object.keys(template.targetWhitelist ?? {}).length)}</Typography>
					</Td>
				);
			},
		},

		installers: {
			sortable: true,
			label: 'Installs',
			...getColumnDefaults('installations', true),
			render(template, props) {
				return (
					<Td>
						<Typography>{formatNumber(Object.keys(template.installationsByTarget ?? {}).length)}</Typography>
					</Td>
				);
			},
		},
		id: {
			sortable: true,
			label: 'ID',
			...getColumnDefaults('id', true),
			render(template, props) {
				return (
					<Td>
						<Typography>
							<Highlight
								text={`${template.id}`}
								search={props?.search ?? ''}
							/>
						</Typography>
					</Td>
				);
			},
		},

		channels: {
			sortable: true,
			label: 'Proposed Channels',
			...getColumnDefaults('channels', true),
			render(template, _) {
				return (
					<Td>
						<CampaignChannelIcons campaign={template?.campaign} />
					</Td>
				);
			},
		},
	} as const;

export const CoMarketingStatusColumnMapping: Record<
	CoMarketingApprovalStatus | 'active' | 'rejected' | 'sent',
	Partial<TagProps & { style: React.CSSProperties }>
> = {
	active: {
		color: 'var(--text-primary)',
		style: {
			borderColor: 'var(--green100)',
			backgroundColor: 'var(--green100)',
			color: 'var(--text-primary)',
		},
		children: 'Active',
	},
	approved: {
		color: 'var(--text-primary)',
		style: {
			borderColor: 'var(--green100)',
			backgroundColor: 'var(--green100)',
			color: 'var(--text-primary)',
		},
		children: 'Approved',
	},
	pending: {
		color: 'var(--text-primary)',
		style: {
			borderColor: 'var(--orange100)',
			backgroundColor: 'var(--orange100)',
			color: 'var(--text-primary)',
		},
		children: 'In Review',
	},
	denied: {
		color: 'var(--text-primary)',
		style: {
			borderColor: 'var(--red100)',
			backgroundColor: 'var(--red100)',
			color: 'var(--text-primary)',
		},
		children: 'Denied',
	},
	rejected: {
		color: 'var(--text-primary)',
		style: {
			borderColor: 'var(--red100)',
			backgroundColor: 'var(--red100)',
			color: 'var(--text-primary)',
		},
		children: 'Rejected',
	},
	sent: {
		color: 'var(--text-primary)',
		style: {
			borderColor: 'var(--gray100)',
			backgroundColor: 'var(--gray100)',
			color: 'var(--text-primary)',
		},
		children: 'Template Sent',
	},
};

export type CoMarketingInstalledColumnKeys = keyof InstalledCoMarketingTemplate | 'name' | 'created' | 'retailers' | 'id' | 'channels' | 'status';

export const CoMarketingTableInstalledHeaders: Partial<Record<CoMarketingInstalledColumnKeys, CoMarketingColumnConfig<InstalledCoMarketingTemplateWithCampaign>>> = {
	status: {
		sortable: true,
		label: 'Status',
		...getColumnDefaults('status', true),
		render(template, _) {
			const useStatus = template.approvalStatus === 'approved' && template.isCampaignActive ? 'active' : template.approvalStatus;
			const tagInfo = CoMarketingStatusColumnMapping[useStatus];
			return (
				<Td>
					<Tag {...tagInfo}>
						<span style={{ color: tagInfo?.style?.color ?? 'var(--text-primary)' }}>{tagInfo?.children}</span>
					</Tag>
				</Td>
			);
		},
	},
	name: {
		sortable: true,
		label: 'Name',
		...getColumnDefaults('name', true),
		render(template, props) {
			return (
				<Td>
					<Highlight
						text={template.campaign?.name ?? ''}
						search={props?.search ?? ''}
					/>
				</Td>
			);
		},
	},

	updated: {
		sortable: true,
		label: 'Updated',
		...getColumnDefaults('updated', true),
		render(template, props) {
			return (
				<Td>
					<Typography>{formatTimestamp(template.updated, { hideTime: true })}</Typography>
					<Typography variant="body-sm">
						{formatTimestamp(template.updated, {
							month: false,
							day: false,
							year: false,
						})}
					</Typography>
				</Td>
			);
		},
	},

	created: {
		sortable: true,
		label: 'Installed',
		...getColumnDefaults('created', true),
		render(template, _) {
			return (
				<Td>
					<Typography>{formatTimestamp(template.created, { hideTime: true })}</Typography>
					<Typography variant="body-sm">
						{formatTimestamp(template.created, {
							month: false,
							day: false,
							year: false,
						})}
					</Typography>
				</Td>
			);
		},
	},

	id: {
		sortable: true,
		label: 'ID',
		...getColumnDefaults('id', true),
		render(template, props) {
			return (
				<Td>
					<Typography>
						<Highlight
							text={`${template.id}`}
							search={props?.search ?? ''}
						/>
					</Typography>
				</Td>
			);
		},
	},

	channels: {
		sortable: true,
		label: 'Channels',
		...getColumnDefaults('channels', true),
		render(template, _) {
			return (
				<Td>
					<CampaignChannelIcons campaign={template?.campaign} />
				</Td>
			);
		},
	},
} as const;
